"use client";

import { c as _c } from "react/compiler-runtime";
import ProfileAvatar, { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ImageScale } from "@/helpers/image.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import Link from "next/link";
interface NextMintCardProps {
  readonly drop: ApiDrop;
}
const formatDropTimestamp = (timestamp: number): string | null => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const dateLabel = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric"
  }).format(date);
  const timeLabel = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(date);
  return `${dateLabel} · ${timeLabel}`;
};
export const NextMintCard = t0 => {
  const $ = _c(31);
  const {
    drop
  } = t0;
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const media = drop.parts[0]?.media[0];
  const title = drop.title ?? drop.metadata.find(_temp)?.data_value ?? "Untitled";
  const author = drop.author;
  let t1;
  if ($[0] !== drop.created_at) {
    t1 = formatDropTimestamp(drop.created_at);
    $[0] = drop.created_at;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const timestamp = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="tw-flex tw-items-center tw-gap-2"><span className="tw-size-1.5 tw-rounded-full tw-bg-emerald-500" /><span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-leading-5 tw-tracking-wide tw-text-emerald-400">NEXT MINT</span></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== timestamp) {
    t3 = timestamp && <span className="tw-font-mono tw-text-xs tw-text-white/50">{timestamp}</span>;
    $[3] = timestamp;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <div className="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-border-b tw-border-white/5 tw-bg-[#111111] tw-px-2.5 tw-py-1.5 @sm:tw-flex-row @sm:tw-items-center @sm:tw-justify-between sm:tw-px-3 sm:tw-py-2">{t2}{t3}</div>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== hasTouchScreen || $[8] !== media) {
    t5 = <div className="tw-relative tw-flex tw-aspect-[3/4] tw-max-h-[clamp(320px,70vw,500px)] tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-bg-black/50 tw-p-2 sm:tw-p-3"><div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-transition-transform tw-duration-700 tw-ease-out group-hover:tw-scale-105">{media ? <DropListItemContentMedia media_mime_type={media.mime_type} media_url={media.url} imageObjectPosition="center" imageScale={ImageScale.AUTOx600} disableAutoPlay={hasTouchScreen} disableModal={hasTouchScreen} /> : <div className="tw-flex tw-size-full tw-items-center tw-justify-center tw-bg-black/40"><span className="tw-text-sm tw-text-white/40">No image</span></div>}</div></div>;
    $[7] = hasTouchScreen;
    $[8] = media;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <div className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/5 tw-bg-[#0c0c0c] tw-transition-colors group-hover:tw-border-white/10">{t4}{t5}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = `/waves?wave=${drop.wave.id}&drop=${drop.id}`;
  let t8;
  if ($[13] !== t7 || $[14] !== title) {
    t8 = <Link href={t7} className="tw-m-0 tw-line-clamp-2 tw-text-base tw-font-semibold tw-leading-tight tw-text-white tw-no-underline tw-transition-colors group-hover:tw-text-white/80 @lg:tw-line-clamp-1">{title}</Link>;
    $[13] = t7;
    $[14] = title;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = `/${author.handle ?? author.primary_address}`;
  const t10 = author.handle ?? "Artist";
  let t11;
  if ($[16] !== author.pfp || $[17] !== t10) {
    t11 = <ProfileAvatar pfpUrl={author.pfp} alt={t10} size={ProfileBadgeSize.SMALL} />;
    $[16] = author.pfp;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = author.handle ?? "Anonymous";
  let t13;
  if ($[19] !== t12) {
    t13 = <span className="tw-min-w-0 tw-truncate tw-text-xs tw-text-white/50 desktop-hover:hover:tw-text-white">{t12}</span>;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t11 || $[22] !== t13 || $[23] !== t9) {
    t14 = <Link href={t9} className="tw-mt-2 tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-no-underline">{t11}{t13}</Link>;
    $[21] = t11;
    $[22] = t13;
    $[23] = t9;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t14 || $[26] !== t8) {
    t15 = <div className="tw-flex tw-flex-col tw-gap-3"><div>{t8}{t14}</div></div>;
    $[25] = t14;
    $[26] = t8;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t15 || $[29] !== t6) {
    t16 = <div className="tw-group tw-flex tw-flex-col tw-gap-3 tw-text-left tw-transition-all tw-duration-300 sm:tw-gap-4">{t6}{t15}</div>;
    $[28] = t15;
    $[29] = t6;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  return t16;
};
function _temp(m) {
  return m.data_key === "title";
}